<template>
  <div class="page">
    <!-- 顶部导航栏：返回按钮 + 标题（按兑奖方式显示 手动兑奖/预约兑奖） -->
    <div class="nav-bar">
      <div class="nav-back" @click="goBack">
        <!-- 返回箭头 -->
        <svg viewBox="0 0 24 24">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </div>
      <div class="nav-title">{{ pageTitle }}</div>
    </div>

    <!-- 彩票类型切换标签：默认快乐8 -->
    <div class="type-tabs">
      <div
        v-for="t in typeList"
        :key="t.value"
        class="type-tab"
        :class="{ active: lotteryType === t.value }"
        @click="switchType(t.value)"
      >
        {{ t.label }}
      </div>
    </div>

    <div class="content">
      <!-- 1. 选择玩法（号码组合模式）：折叠框，点标题展开/收起，选中后自动收起 -->
      <div class="section">
        <div class="collapse-head" @click="toggleModePanel">
          <span class="section-title">选择号码组合模式</span>
          <span class="collapse-sum">
            <template v-if="playType">{{ playType }} · {{ modeDesc(playType) }}</template>
            <span v-else class="collapse-placeholder">请选择</span>
            <!-- 箭头：展开时旋转 180 度 -->
            <svg class="collapse-arrow" :class="{ open: modePanelOpen }" viewBox="0 0 24 24">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </div>

        <!-- 折叠内容区：max-height 过渡动画实现展开/收起 -->
        <div class="collapse-body" :class="{ open: modePanelOpen }">
          <div v-if="currentModes.length" class="mode-grid">
            <div
              v-for="m in currentModes"
              :key="m"
              class="mode-item"
              :class="{ active: playType === m }"
              @click="selectMode(m)"
            >
              <div class="mode-value">{{ m }}</div>
              <div class="mode-desc">{{ modeDesc(m) }}</div>
            </div>
          </div>
          <div v-else class="mode-empty">该彩票类型组合模式暂未开放，敬请期待</div>
        </div>
      </div>

      <!-- 2. 填写要兑奖的期号（如 2026263） -->
      <div class="section">
        <div class="section-title">兑奖期号</div>
        <input
          v-model="betNumIssue"
          type="text"
          inputmode="numeric"
          class="expect-input"
          placeholder="请输入期号，如 2026263"
        />
      </div>

      <!-- 3. 手动填写号码：每个号码一个独立小方框，与购买彩票的手动输入页一致 -->
      <div v-if="currentModes.length" class="section">
        <div class="section-title">手动填写号码</div>
        <div class="boxes-panel" @paste="onPasteFill">
          <!-- 主号码组：红球/前区/快乐8号码，红色描边 -->
          <div v-if="mainGroupLabel" class="group-label">{{ mainGroupLabel }}</div>
          <div class="boxes-row">
            <input
              v-for="i in mainCount"
              :key="'m' + i"
              ref="mainInputs"
              :value="mainArr[i - 1]"
              type="text"
              inputmode="numeric"
              :maxlength="boxMaxLen"
              class="num-box red"
              @input="onBoxInput('main', i - 1, $event)"
              @keydown.delete="onBoxBackspace('main', i - 1)"
              @keyup.enter="handleSubmit"
              @focus="onBoxFocus($event)"
            />
          </div>

          <!-- 辅助号码组：蓝球/后区，蓝色描边，前面加 + 号 -->
          <template v-if="extraCount">
            <div class="extra-split">
              <span class="split-plus">+</span>
              <span class="group-label">{{ extraGroupLabel }}</span>
            </div>
            <div class="boxes-row">
              <input
                v-for="i in extraCount"
                :key="'e' + i"
                ref="extraInputs"
                :value="extraArr[i - 1]"
                type="text"
                inputmode="numeric"
              :maxlength="boxMaxLen"
              class="num-box blue"
                @input="onBoxInput('extra', i - 1, $event)"
                @keydown.delete="onBoxBackspace('extra', i - 1)"
                @keyup.enter="handleSubmit"
                @focus="onBoxFocus($event)"
              />
            </div>
          </template>
        </div>
        <div class="manual-tip">{{ manualTip }}</div>
      </div>

      <!-- 4. 是否保存到号码列表：仅手动兑奖（manual）时显示，默认不勾选=false -->
      <div v-if="prizeType === 'manual'" class="section">
        <label class="save-label" @click.prevent="saveNumListFlag = !saveNumListFlag">
          <span class="checkbox" :class="{ checked: saveNumListFlag }">
            <svg v-if="saveNumListFlag" viewBox="0 0 24 24">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          <span>保存到号码列表</span>
          <span class="save-hint">（不勾选则本次兑奖不记录）</span>
        </label>
      </div>

      <!-- 5. 是否邮箱通知：仅预约兑奖（reserve）时显示，默认不勾选=false -->
      <div v-if="prizeType === 'reserve'" class="section">
        <label class="save-label" @click.prevent="emailNotification = !emailNotification">
          <span class="checkbox" :class="{ checked: emailNotification }">
            <svg v-if="emailNotification" viewBox="0 0 24 24">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          <span>邮箱通知</span>
          <span class="save-hint">（开奖结果发送到您的邮箱）</span>
        </label>
      </div>

      <!-- 提交按钮 -->
      <button
        class="submit-btn"
        :disabled="loading || !currentModes.length"
        @click="handleSubmit"
      >
        {{ loading ? '提交中...' : pageTitle }}
      </button>
    </div>

    <!-- ===== 兑奖结果弹窗 ===== -->
    <div v-if="showResult" class="modal-mask" @click.self="closeResult">
      <div class="modal-box">
        <div class="modal-head">
          <span class="modal-title">兑奖结果</span>
          <span class="modal-close" @click="closeResult">×</span>
        </div>

        <!-- 回显用户填写的信息 -->
        <div class="modal-info">
          <div class="info-row">
            <span class="info-label">兑奖方式</span>
            <span class="info-value">{{ pageTitle }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">彩票类型</span>
            <span class="info-value">{{ typeLabel }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">兑奖期号</span>
            <span class="info-value">第 {{ betNumIssue }} 期</span>
          </div>
          <div class="info-row">
            <span class="info-label">玩法</span>
            <span class="info-value">{{ playType }}</span>
          </div>
        </div>

        <!-- 结果描述：后端返回的一段文字，根据是否中奖显示不同颜色 -->
        <div class="result-text" :class="{ win: isWin, lose: !isWin }">
          {{ resultText }}
        </div>

        <div class="modal-foot">
          <button class="foot-btn plain" @click="closeResult">关 闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 导入兑奖接口（手动/预约共用）
import { numPrize } from '@/api/lottery'
// 彩票类型枚举统一从工具文件引入
import { lotteryTypeList, lotteryTypeLabel, lotteryModeMap, plsModeDescMap } from '@/utils/lottery'

export default {
  name: 'NumPrizeView',
  data() {
    return {
      // 兑奖方式：manual=手动兑奖，reserve=预约兑奖（从路由参数带入，默认手动）
      prizeType: this.$route.query.prizeType === 'reserve' ? 'reserve' : 'manual',
      // 是否邮箱通知：默认 false，仅预约兑奖时展示该选项
      emailNotification: false,
      // 当前选中的彩票类型，默认双色球（请求参数名 lotteryType）
      lotteryType: 'ssq',
      // 彩票类型枚举（统一维护在 @/utils/lottery）
      typeList: lotteryTypeList,
      // 各彩票类型支持的组合模式（统一维护在 @/utils/lottery）
      modeMap: lotteryModeMap,
      // 当前玩法/组合模式，默认双色球的 '7+1'（请求参数名 playType）
      playType: '7+1',
      // 组合模式折叠面板是否展开：默认收起，点击标题后展开
      modePanelOpen: false,
      // 兑奖期号（用户手填，提交时转成数字）
      betNumIssue: '',
      // 是否保存到号码列表，默认 false
      saveNumListFlag: false,
      // 号码方框数组
      mainArr: [],
      extraArr: [],
      loading: false,
      showResult: false,
      resultText: ''
    }
  },
  computed: {
    // 页面标题/按钮文字：按兑奖方式显示
    pageTitle() {
      return this.prizeType === 'reserve' ? '预约兑奖' : '手动兑奖'
    },
    // 主号码组需要填几个：快乐8取玩法数值，双色球/大乐透取 + 前面的数，排列三取 + 后面的数
    mainCount() {
      const mode = this.playType
      if (!mode) return 0
      // 排列三：玩法形如 direct+3，加号后面的数字就是号码个数（3=单式，4=复式）
      if (this.lotteryType === 'pls') {
        return Number(mode.split('+')[1]) || 0
      }
      return Number(mode.indexOf('+') > -1 ? mode.split('+')[0] : mode)
    },
    // 辅助号码组需要填几个：双色球/大乐透取 + 后面的数；排列三的 + 是玩法名一部分，没有辅助组
    extraCount() {
      const mode = this.playType
      if (!mode) return 0
      if (this.lotteryType === 'pls') return 0
      return mode.indexOf('+') > -1 ? Number(mode.split('+')[1]) : 0
    },
    // 每个方框最多填几位：排列三每位就是一个数字（0-9）只填 1 位，其他类型最多 2 位
    boxMaxLen() {
      return this.lotteryType === 'pls' ? 1 : 2
    },
    // 号码最小值：排列三每位可以是 0，其他类型号码从 1 开始
    numMin() {
      return this.lotteryType === 'pls' ? 0 : 1
    },
    // 同组号码是否允许重复：排列三直选/组选3允许重复（组选6必须互不相同），其他类型不允许
    allowRepeatNums() {
      if (this.lotteryType === 'pls') {
        return this.playType.indexOf('group6') !== 0
      }
      return false
    },
    mainGroupLabel() {
      const map = { ssq: '红球', dlt: '前区' }
      return map[this.lotteryType] || ''
    },
    extraGroupLabel() {
      const map = { ssq: '蓝球', dlt: '后区' }
      return map[this.lotteryType] || ''
    },
    currentModes() {
      return this.modeMap[this.lotteryType] || []
    },
    typeLabel() {
      return lotteryTypeLabel(this.lotteryType)
    },
    manualTip() {
      const mode = this.playType
      // 快乐8：模式值就是要填的号码个数，范围 1-80
      if (this.lotteryType === 'klb') {
        return `请在下面 ${mode} 个方框中各填一个号码（范围 1-80，号码不可重复），填满两位数字会自动跳到下一个框`
      }
      // 七乐彩：30选7，范围 1-30
      if (this.lotteryType === 'qlc') {
        return `请在下面 ${mode} 个方框中各填一个号码（范围 1-30，号码不可重复），填满两位数字会自动跳到下一个框`
      }
      // 排列三：每个方框填 1 位数字（0-9），直选/组选3可重复，组选6不可重复
      if (this.lotteryType === 'pls') {
        const count = mode.split('+')[1] || ''
        const repeatTip = mode.indexOf('group6') === 0 ? '号码不可重复' : '允许重复'
        return `${plsModeDescMap[mode] || mode}：请在下面 ${count} 个方框中各填 1 位数字（0-9，${repeatTip}），填满 1 位会自动跳到下一个框`
      }
      if (mode.indexOf('+') > -1) {
        const parts = mode.split('+')
        if (this.lotteryType === 'ssq') {
          return `红框填 ${parts[0]} 个红球（1-33），蓝框填 ${parts[1]} 个蓝球（1-16），同组内号码不可重复`
        }
        if (this.lotteryType === 'dlt') {
          return `红框填 ${parts[0]} 个前区号码（1-35），蓝框填 ${parts[1]} 个后区号码（1-12），同组内号码不可重复`
        }
      }
      return '请在方框中填写号码'
    },
    // 是否中奖：结果文字包含“中奖”且不包含“未中奖”视为中奖
    isWin() {
      const txt = this.resultText || ''
      return txt.indexOf('中奖') > -1 && txt.indexOf('未中奖') === -1
    }
  },
  created() {
    this.initBoxes()
  },
  watch: {
    // 切换玩法后方框数量重新生成（切换彩票类型时玩法在 switchType 里重置）
    playType() {
      this.initBoxes()
    }
  },
  methods: {
    // 按当前组合模式生成对应数量的空方框
    initBoxes() {
      this.mainArr = new Array(this.mainCount).fill('')
      this.extraArr = new Array(this.extraCount).fill('')
    },

    // 切换彩票类型：玩法默认选中该类型第一项（面板保持收起）
    switchType(value) {
      if (this.lotteryType === value) return
      this.lotteryType = value
      const modes = this.modeMap[value] || []
      this.playType = modes.length ? modes[0] : ''
      this.modePanelOpen = false
    },

    // 展开/收起组合模式折叠面板（暂未开放的类型不响应）
    toggleModePanel() {
      if (!this.currentModes.length) return
      this.modePanelOpen = !this.modePanelOpen
    },

    // 选择某个玩法：赋值后自动收起面板
    selectMode(m) {
      this.playType = m
      this.modePanelOpen = false
    },

    // 组合模式文字说明
    modeDesc(mode) {
      // 快乐8、七乐彩：选项就是选号个数
      if (this.lotteryType === 'klb' || this.lotteryType === 'qlc') {
        return `选 ${mode} 个号码`
      }
      // 排列三：玩法说明统一取自枚举表 plsModeDescMap
      if (this.lotteryType === 'pls') {
        return plsModeDescMap[mode] || mode
      }
      if (mode.indexOf('+') > -1) {
        const parts = mode.split('+')
        if (this.lotteryType === 'ssq') return `${parts[0]} 个红球 + ${parts[1]} 个蓝球`
        if (this.lotteryType === 'dlt') return `${parts[0]} 个前区 + ${parts[1]} 个后区`
      }
      return ''
    },

    // 小方框输入：只留数字、最多两位（排列三只留一位），填满自动跳下一框
    onBoxInput(group, index, e) {
      const val = e.target.value.replace(/\D/g, '').slice(0, this.boxMaxLen)
      const arrName = group === 'main' ? 'mainArr' : 'extraArr'
      this.$set(this[arrName], index, val)
      if (val.length === this.boxMaxLen) {
        const count = group === 'main' ? this.mainCount : this.extraCount
        if (index + 1 < count) {
          this.focusBox(group, index + 1)
        } else if (group === 'main' && this.extraCount) {
          this.focusBox('extra', 0)
        }
      }
    },

    // 退格：当前框为空时跳回上一框
    onBoxBackspace(group, index) {
      const arr = group === 'main' ? this.mainArr : this.extraArr
      if (arr[index]) return
      if (index > 0) {
        this.focusBox(group, index - 1)
      } else if (group === 'extra') {
        this.focusBox('main', this.mainCount - 1)
      }
    },

    focusBox(group, index) {
      const refs = this.$refs[group === 'main' ? 'mainInputs' : 'extraInputs']
      if (refs && refs[index]) {
        refs[index].focus()
        refs[index].select()
      }
    },

    onBoxFocus(e) {
      e.target.select()
    },

    // 粘贴填充：支持整串号码自动分配到各方框
    onPasteFill(e) {
      e.preventDefault()
      const text = (e.clipboardData || window.clipboardData).getData('text') || ''
      if (!text) return
      // 排列三：粘贴 "120" 或 "1,2,0" 都按单个数字拆开，逐位分配到方框
      if (this.lotteryType === 'pls') {
        this.fillGroup('main', text.replace(/\D/g, '').split(''))
        this.$nextTick(() => {
          const emptyMain = this.mainArr.findIndex(v => !v)
          if (emptyMain > -1) this.focusBox('main', emptyMain)
        })
        return
      }
      if (this.extraCount && text.indexOf('+') > -1) {
        const parts = text.split('+')
        this.fillGroup('main', parts[0].match(/\d+/g) || [])
        this.fillGroup('extra', (parts[1] || '').match(/\d+/g) || [])
      } else {
        const nums = text.match(/\d+/g) || []
        this.fillGroup('main', nums.slice(0, this.mainCount))
        if (this.extraCount) {
          this.fillGroup('extra', nums.slice(this.mainCount, this.mainCount + this.extraCount))
        }
      }
      this.$nextTick(() => {
        const emptyMain = this.mainArr.findIndex(v => !v)
        if (emptyMain > -1) this.focusBox('main', emptyMain)
        else if (this.extraCount) {
          const emptyExtra = this.extraArr.findIndex(v => !v)
          if (emptyExtra > -1) this.focusBox('extra', emptyExtra)
        }
      })
    },

    fillGroup(group, nums) {
      const arrName = group === 'main' ? 'mainArr' : 'extraArr'
      const count = group === 'main' ? this.mainCount : this.extraCount
      for (let i = 0; i < count; i++) {
        this.$set(this[arrName], i, nums[i] ? String(nums[i]).slice(0, this.boxMaxLen) : '')
      }
    },

    // 校验一组号码：纯数字、在 min~max 范围内、默认不重复（可放开）
    // min：允许的最小值（排列三为 0）；allowRepeat：是否允许重复（排列三直选/组选3允许）
    validateNumGroup(tokens, max, label, min, allowRepeat) {
      if (min === undefined) min = 1
      if (!tokens.length) {
        alert(`请填写${label}号码`)
        return false
      }
      const used = []
      for (const token of tokens) {
        if (!/^\d+$/.test(token)) {
          alert(`${label}号码只能填写数字，“${token}”不合法`)
          return false
        }
        const n = Number(token)
        if (n < min || n > max) {
          alert(`${label}号码范围为 ${min}-${max}，“${n}”不合法`)
          return false
        }
        // 同一组内号码默认不能重复（排列三直选/组选3通过 allowRepeat 放开）
        if (!allowRepeat && used.indexOf(n) > -1) {
          alert(`${label}号码不能重复：${n}`)
          return false
        }
        used.push(n)
      }
      return true
    },

    // 把方框里的号码校验并拼成后端要求的 betNumDTOs 数组
    // redNum 主号码英文逗号拼接；blueNum 辅助号码英文逗号拼接，没有则为 null
    // 校验通过返回如 [{ redNum: '11,13,...,70', blueNum: null }]；不通过返回 null
    buildBetDTOs() {
      if (!this.mainArr.length) {
        alert('请先填写号码')
        return null
      }
      const rangeMap = {
        klb: { main: 80 },
        ssq: { main: 33, extra: 16 },
        dlt: { main: 35, extra: 12 },
        qlc: { main: 30 },
        pls: { main: 9 } // 排列三：每位就是一个数字 0-9
      }
      const range = rangeMap[this.lotteryType] || { main: 99, extra: 99 }
      const mainLabel = this.mainGroupLabel || ''
      const extraLabel = this.extraGroupLabel || ''

      const mainTokens = this.mainArr.map(v => (v || '').trim())
      const extraTokens = this.extraArr.map(v => (v || '').trim())

      if (mainTokens.some(v => !v)) {
        alert(`请把${mainLabel}号码全部填写完整`)
        return null
      }
      if (this.extraCount && extraTokens.some(v => !v)) {
        alert(`请把${extraLabel}号码全部填写完整`)
        return null
      }
      // 数字、范围、重复校验（排列三最小 0；直选/组选3允许重复，组选6不允许）
      if (!this.validateNumGroup(mainTokens, range.main, mainLabel, this.numMin, this.allowRepeatNums)) return null
      if (this.extraCount && !this.validateNumGroup(extraTokens, range.extra, extraLabel)) return null

      // 拼成投注号码对象；没有辅助号码组时 blueNum 传 null
      const redNum = mainTokens.map(n => String(Number(n))).join(',')
      const blueNum = this.extraCount
        ? extraTokens.map(n => String(Number(n))).join(',')
        : null
      return [{ redNum, blueNum }]
    },

    // 提交兑奖（手动/预约共用，prizeType 区分）
    async handleSubmit() {
      if (this.loading || !this.playType) return
      // 期号必须填写且为纯数字
      if (!this.betNumIssue || !/^\d+$/.test(String(this.betNumIssue).trim())) {
        alert('请输入正确的兑奖期号（纯数字）')
        return
      }
      // 校验并拼好号码
      const betNumDTOs = this.buildBetDTOs()
      if (!betNumDTOs) return

      this.loading = true
      try {
        const res = await numPrize({
          lotteryType: this.lotteryType,
          prizeType: this.prizeType,
          playType: this.playType,
          // 期号按接口要求传数字
          betNumIssue: Number(String(this.betNumIssue).trim()),
          betNumDTOs: betNumDTOs,
          saveNumListFlag: this.saveNumListFlag,
          // 邮箱通知仅预约兑奖生效，手动兑奖固定传 false
          emailNotification: this.prizeType === 'reserve' ? this.emailNotification : false
        })
        // 后端返回：{ code: "200", data: "兑奖结果描述", msg, success }
        if (res.code === '200' || res.success) {
          this.resultText = res.data || ''
          this.showResult = true
        } else {
          alert(res.msg || '兑奖失败，请稍后重试')
        }
      } catch (err) {
        console.error('兑奖失败：', err)
        alert('网络错误，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    closeResult() {
      this.showResult = false
    },

    goBack() {
      this.$router.back()
    }
  }
}
</script>

<style scoped>
.page {
  width: 100%;
  max-width: 480px;
  min-height: 100%;
  margin: 0 auto;
  background: #f5f6fa;
}

.nav-bar {
  position: relative;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-back {
  position: absolute;
  left: 10px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.nav-back svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: #fff;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.nav-title {
  color: #fff;
  font-size: 16px;
}

.type-tabs {
  display: flex;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  /* 类型超过 4 个时横向滑动查看，一屏最多显示 4 个 */
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch; /* iOS 滑动惯性 */
  scrollbar-width: none; /* Firefox 隐藏滚动条 */
}

/* Chrome/Safari 隐藏滚动条但保留滑动能力 */
.type-tabs::-webkit-scrollbar {
  display: none;
}

.type-tab {
  /* 固定占 1/4 宽度，一屏正好 4 个；超出部分横向滑动，不被挤压 */
  flex: 0 0 25%;
  box-sizing: border-box;
  text-align: center;
  padding: 12px 4px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
}

.type-tab.active {
  color: #667eea;
  font-weight: bold;
}

.type-tab.active::after {
  content: '';
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  width: 28px;
  height: 3px;
  border-radius: 2px;
  background: #667eea;
}

.content {
  padding: 16px 12px;
}

.section {
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 16px;
}

.section-title {
  font-size: 15px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 14px;
}

/* ===== 组合模式折叠框 ===== */
/* 标题行：标题在左、当前选择+箭头在右，整行可点 */
.collapse-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}

.collapse-head .section-title {
  margin-bottom: 0;
}

/* 右侧当前选择摘要：红色字 */
.collapse-sum {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #f56c6c;
  font-weight: bold;
  max-width: 60%;
}

/* 未选择时的灰色提示 */
.collapse-placeholder {
  color: #c0c4cc;
  font-weight: normal;
}

/* 折叠箭头：默认朝下，展开时旋转 180 度变朝上 */
.collapse-arrow {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  fill: none;
  stroke: #909399;
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: transform 0.25s ease;
}

.collapse-arrow.open {
  transform: rotate(180deg);
}

/* 折叠内容区：用 max-height 过渡做展开/收起动画 */
.collapse-body {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

/* 展开时：高度足够放下所有玩法选项（快乐8三行最高） */
.collapse-body.open {
  max-height: 500px;
}

/* 展开后内容与标题留出间距 */
.collapse-body.open .mode-grid,
.collapse-body.open .mode-empty {
  margin-top: 14px;
}

.mode-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.mode-item {
  width: calc((100% - 30px) / 4);
  border: 1.5px solid #dcdfe6;
  border-radius: 8px;
  padding: 8px 4px;
  text-align: center;
  cursor: pointer;
  box-sizing: border-box;
}

.mode-item.active {
  border-color: #f56c6c;
  background: #fef0f0;
}

.mode-value {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.mode-item.active .mode-value {
  color: #f56c6c;
}

.mode-desc {
  font-size: 10px;
  color: #909399;
  margin-top: 3px;
  line-height: 1.3;
}

.mode-empty {
  text-align: center;
  color: #909399;
  font-size: 13px;
  padding: 30px 0;
}

/* 期号输入框 */
.expect-input {
  width: 100%;
  box-sizing: border-box;
  height: 44px;
  padding: 0 12px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s;
}

.expect-input:focus {
  border-color: #f56c6c;
}

/* ===== 手动填写号码（一个个独立小方框） ===== */
.boxes-panel {
  border: 1px solid #f0d0d0;
  border-radius: 10px;
  padding: 12px;
}

.group-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 8px;
}

.boxes-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.num-box {
  width: 40px;
  height: 40px;
  border: 1.5px solid #dcdfe6;
  border-radius: 8px;
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  color: #303133;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.num-box.red {
  border-color: #f8a3a3;
  color: #f56c6c;
}

.num-box.red:focus {
  border-color: #f56c6c;
  box-shadow: 0 0 0 2px rgba(245, 108, 108, 0.15);
}

.num-box.blue {
  border-color: #a0cfff;
  color: #409eff;
}

.num-box.blue:focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
}

.extra-split {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0 8px;
}

.split-plus {
  font-size: 16px;
  font-weight: bold;
  color: #909399;
}

.extra-split .group-label {
  margin-bottom: 0;
}

.manual-tip {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.6;
  color: #909399;
}

/* ===== 勾选框（保存到号码列表 / 邮箱通知共用样式） ===== */
.save-label {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #303133;
  cursor: pointer;
  user-select: none;
}

.checkbox {
  width: 18px;
  height: 18px;
  border: 1.5px solid #c0c4cc;
  border-radius: 3px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.checkbox.checked {
  background: #667eea;
  border-color: #667eea;
}

.checkbox svg {
  width: 13px;
  height: 13px;
  fill: none;
  stroke: #fff;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.save-hint {
  margin-left: 8px;
  font-size: 12px;
  color: #909399;
}

/* ===== 提交按钮 ===== */
.submit-btn {
  width: 100%;
  height: 46px;
  border: none;
  border-radius: 23px;
  background: linear-gradient(135deg, #f78a8a 0%, #f56c6c 100%);
  color: #fff;
  font-size: 17px;
  font-weight: bold;
  letter-spacing: 4px;
  cursor: pointer;
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ===== 结果弹窗 ===== */
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  padding: 20px;
}

.modal-box {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 14px;
}

.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.modal-close {
  font-size: 22px;
  line-height: 1;
  color: #909399;
  cursor: pointer;
  padding: 0 4px;
}

.modal-info {
  padding: 12px 16px 4px;
}

.info-row {
  display: flex;
  font-size: 13px;
  line-height: 2;
}

.info-label {
  width: 70px;
  color: #909399;
}

.info-value {
  color: #303133;
  font-weight: bold;
}

/* 兑奖结果文字：中奖绿色底，未中奖灰色底 */
.result-text {
  margin: 12px 16px;
  padding: 14px;
  border-radius: 8px;
  font-size: 15px;
  line-height: 1.6;
  font-weight: bold;
  text-align: center;
  word-break: break-all;
}

.result-text.win {
  background: #f0f9eb;
  color: #67c23a;
}

.result-text.lose {
  background: #f4f4f5;
  color: #909399;
}

.modal-foot {
  display: flex;
  gap: 10px;
  padding: 0 16px 16px;
}

.foot-btn {
  flex: 1;
  height: 40px;
  border-radius: 20px;
  font-size: 15px;
  cursor: pointer;
}

.foot-btn.plain {
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #606266;
}

@media screen and (max-width: 360px) {
  .num-box {
    width: 34px;
    height: 34px;
  }
  .boxes-row {
    gap: 6px;
  }
  .mode-desc {
    font-size: 9px;
  }
}
</style>
